/**
 * JSON fetch helper with optional timeout. Throws HttpError on non-OK responses.
 */
export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    if (typeof o.error === 'string') return o.error;
    if (typeof o.message === 'string') return o.message;
  }
  return fallback;
}

export async function fetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number }
): Promise<T> {
  const { timeoutMs, signal: externalSignal, ...rest } = init ?? {};
  const controller = timeoutMs != null ? new AbortController() : undefined;
  const timeoutId =
    controller && timeoutMs != null
      ? setTimeout(() => controller.abort(), timeoutMs)
      : undefined;

  const requestInit: RequestInit = { ...rest };
  if (controller) {
    requestInit.signal = controller.signal;
  } else if (externalSignal != null) {
    requestInit.signal = externalSignal;
  }

  try {
    const res = await fetch(input, requestInit);
    const text = await res.text();
    let data: unknown;
    if (text) {
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        data = { raw: text };
      }
    }
    if (!res.ok) {
      const msg = messageFromBody(data, `HTTP ${res.status}`);
      throw new HttpError(msg, res.status, data);
    }
    return (data ?? {}) as T;
  } catch (err) {
    if (err instanceof HttpError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}
